/**
 * Valores de una lista en el orden del árbol de /lists: cada raíz seguida de sus hijos,
 * y los hermanos por orden alfabético (el GET de listas los ordena así). Sirve para
 * que un selector muestre la misma jerarquía que el editor de la lista.
 *
 * El `parent` de un valor puede ser el _id del padre o su etiqueta (listas viejas):
 * se resuelve igual que en el editor, primero por etiqueta y después por id. Un valor
 * cuyo padre no está en la lista queda como raíz.
 *
 * @param {Array<object>} values
 * @param {{ label?: (value: object) => string }} [options] cómo leer la etiqueta
 * @returns {Array<{ value: object, depth: number }>}
 */
export function listValuesInTreeOrder(values, { label = (v) => v && v.label } = {}) {
  const items = (Array.isArray(values) ? values : []).filter(
    (v) => v && typeof v === "object" && String(label(v) || "").trim()
  );
  const byLabel = new Map();
  const byId = new Map();
  items.forEach((v) => {
    byLabel.set(String(label(v)), v);
    if (v._id != null) byId.set(String(v._id), v);
  });

  const parentOf = (v) => {
    if (v.parent == null || v.parent === "") return null;
    const key = String(v.parent);
    const parent = byLabel.get(key) || byId.get(key) || null;
    return parent === v ? null : parent;
  };

  const children = new Map();
  const roots = [];
  items.forEach((v) => {
    const parent = parentOf(v);
    if (!parent) {
      roots.push(v);
      return;
    }
    if (!children.has(parent)) children.set(parent, []);
    children.get(parent).push(v);
  });

  const byName = (a, b) =>
    String(label(a) || "")
      .toLowerCase()
      .localeCompare(String(label(b) || "").toLowerCase());

  const out = [];
  const visited = new Set();
  const walk = (v, depth) => {
    if (visited.has(v)) return;
    visited.add(v);
    out.push({ value: v, depth });
    (children.get(v) || []).sort(byName).forEach((child) => walk(child, depth + 1));
  };
  roots.sort(byName).forEach((v) => walk(v, 0));
  // Un ciclo de padres deja valores sin alcanzar desde ninguna raíz: van al final.
  items.filter((v) => !visited.has(v)).sort(byName).forEach((v) => walk(v, 0));
  return out;
}
